import React, { useState, useEffect, useCallback, useRef } from "react";
import { Modal, Button, Tag, Progress } from "antd";
import {
	RocketOutlined,
	TrophyOutlined,
	InfoCircleOutlined,
	GiftOutlined,
} from "@ant-design/icons";
import { useHistory, useLocation } from "react-router-dom";
import { APP_PREFIX_PATH } from "configs/AppConfig";
import { extractYouTubeVideoId } from "utils/YouTubeUtil";
import jornadaService from "services/JornadaService";
import useJornadaSocket from "hooks/useJornadaSocket";
import FogosArtificio from "components/atom/FogosArtificio";
import CarrosselImagens from "components/atom/CarrosselImagens";

/**
 * Indicador fixo no header (alinhado à esquerda, todas as telas) com o próximo
 * passo da Jornada do Profissional. Ao concluir um passo (via socket), abre um
 * modal de comemoração com fogos de artifício, o progresso, o próximo passo em
 * destaque e um CTA. Ao fechar o modal, o indicador do header avança.
 */
const NavJornada = ({ isMobile }) => {
	const history = useHistory();
	const location = useLocation();
	const [dados, setDados] = useState(null);
	const [modalVisivel, setModalVisivel] = useState(false);
	const [fogosAtivos, setFogosAtivos] = useState(false);
	const [celebracao, setCelebracao] = useState(null);
	const [celebracaoVisivel, setCelebracaoVisivel] = useState(false);

	// Na tela de início rápido a jornada é suprimida (o indicador do header e o
	// modal de comemoração ao concluir um passo); o onboarding já conduz o
	// profissional ali. A ref permite ao callback do socket ler a rota atual
	// no momento em que o passo é concluído, sem re-subscrever o socket.
	const naTelaInicioRapido = location.pathname.startsWith(
		`${APP_PREFIX_PATH}/inicio-rapido`,
	);
	const naTelaInicioRapidoRef = useRef(naTelaInicioRapido);
	naTelaInicioRapidoRef.current = naTelaInicioRapido;

	const carregar = useCallback(async () => {
		try {
			const resposta = await jornadaService.obterProximoPasso();
			setDados(resposta);
		} catch (e) {
			// Silencioso: o indicador é acessório e não deve travar o header.
			setDados(null);
		}
	}, []);

	// Revalida ao montar e a cada navegação (fallback caso o socket não chegue).
	useEffect(() => {
		carregar();
	}, [carregar, location.pathname]);

	// Conclusão de passo em tempo real: abre o modal de comemoração + fogos.
	const aoConcluirPasso = useCallback((payload) => {
		// Não interromper o fluxo de onboarding do início rápido com o modal
		// de comemoração; nas demais telas ele continua aparecendo normalmente.
		if (naTelaInicioRapidoRef.current) return;
		setCelebracao({
			titulo: payload?.passo_concluido?.titulo || "Passo concluído",
			jornadaConcluida: !!payload?.jornada_concluida,
			recompensa: payload?.recompensa || null,
			total: payload?.total,
			concluidos: payload?.concluidos,
			proximo: payload?.proximo_passo || null,
		});
		setCelebracaoVisivel(true);
		// Fogos contínuos: só param quando o usuário fecha o modal (fecharCelebracao).
		setFogosAtivos(true);
	}, []);

	useJornadaSocket(aoConcluirPasso);

	// Fecha a comemoração e só então avança o indicador do header.
	const fecharCelebracao = () => {
		setCelebracaoVisivel(false);
		setFogosAtivos(false);
		if (celebracao) {
			setDados({
				concluida: celebracao.jornadaConcluida,
				total: celebracao.total,
				concluidos: celebracao.concluidos,
				passo: celebracao.proximo,
			});
		}
	};

	const mostrarIndicador = !!(
		dados &&
		!dados.concluida &&
		dados.passo &&
		!naTelaInicioRapido
	);

	const irParaJornada = () => {
		setModalVisivel(false);
		history.push(`${APP_PREFIX_PATH}/jornada`);
	};

	const passo = mostrarIndicador ? dados.passo : null;
	const videoId =
		passo && passo.url_video
			? extractYouTubeVideoId(passo.url_video)
			: null;
	const embedUrl = videoId
		? `https://www.youtube.com/embed/${videoId}`
		: null;

	const pctConcluido =
		celebracao && celebracao.total
			? Math.round((celebracao.concluidos / celebracao.total) * 100)
			: 0;

	return (
		<>
			{fogosAtivos && (
				<FogosArtificio
					ativo={fogosAtivos}
					altura={
						typeof window !== "undefined" ? window.innerHeight : 600
					}
				/>
			)}

			{/* Modal de comemoração ao concluir um passo */}
			<Modal
				open={celebracaoVisivel}
				onCancel={fecharCelebracao}
				footer={null}
				centered
				width={460}
				style={{ maxWidth: "92vw" }}
				maskStyle={{ backgroundColor: "rgba(170, 170, 170, 0.70)" }}
				maskClosable={false}
				zIndex={1900}
			>
				{celebracao && (
					<div style={{ textAlign: "center", padding: "8px 4px" }}>
						<TrophyOutlined
							style={{ fontSize: 56, color: "#ff8034" }}
						/>
						<h2 style={{ marginTop: 12, marginBottom: 2 }}>
							{celebracao.jornadaConcluida
								? "Jornada concluída! 🎉"
								: "Passo concluído! 🎉"}
						</h2>
						<p style={{ color: "#8c8c8c", marginBottom: 18 }}>
							{celebracao.titulo}
						</p>

						<Progress
							percent={pctConcluido}
							strokeColor="#ff8034"
							showInfo={false}
							style={{ marginBottom: 6 }}
						/>
						<div
							style={{
								marginBottom: 18,
								color: "#595959",
								fontWeight: 600,
								fontSize: 13,
							}}
						>
							{celebracao.concluidos}/{celebracao.total} passos
							concluídos
						</div>

						{!celebracao.jornadaConcluida &&
							celebracao.total - celebracao.concluidos > 0 && (
								<div
									style={{
										marginBottom: 18,
										color: "#ad6800",
										fontWeight: 600,
										fontSize: 13,
									}}
								>
									<GiftOutlined style={{ marginRight: 6 }} />
									Realize mais{" "}
									{celebracao.total -
										celebracao.concluidos}{" "}
									{celebracao.total -
										celebracao.concluidos ===
									1
										? "passo"
										: "passos"}{" "}
									e ganhe R$40
								</div>
							)}

						{celebracao.jornadaConcluida ? (
							<div
								style={{
									background: "#fff7e6",
									border: "1px solid #ffd591",
									borderRadius: 10,
									padding: "14px 16px",
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									gap: 8,
									fontWeight: 600,
									color: "#ad6800",
								}}
							>
								<GiftOutlined style={{ fontSize: 18 }} />
								Você ganhou{" "}
								{celebracao.recompensa
									? celebracao.recompensa.descricao
									: "sua recompensa"}
							</div>
						) : (
							celebracao.proximo && (
								<div
									style={{
										background: "#fafafa",
										border: "1px solid #f0f0f0",
										borderRadius: 10,
										padding: "12px 16px",
										textAlign: "left",
									}}
								>
									<div
										style={{
											fontSize: 11,
											letterSpacing: 0.5,
											color: "#999",
											textTransform: "uppercase",
											marginBottom: 4,
										}}
									>
										Próximo passo
									</div>
									<div
										style={{
											fontWeight: 600,
											fontSize: 16,
											display: "flex",
											alignItems: "center",
											gap: 8,
										}}
									>
										<RocketOutlined
											style={{ color: "#ff8034" }}
										/>
										{celebracao.proximo.titulo}
									</div>
									{celebracao.proximo.descricao && (
										<div
											className="jornada-descricao-html"
											style={{
												marginTop: 6,
												fontSize: 13,
												color: "#595959",
											}}
											dangerouslySetInnerHTML={{
												__html: celebracao.proximo
													.descricao,
											}}
										/>
									)}
								</div>
							)
						)}

						<div
							style={{
								marginTop: 22,
								display: "flex",
								gap: 8,
								justifyContent: "center",
							}}
						>
							<Button
								onClick={() => {
									fecharCelebracao();
									history.push(`${APP_PREFIX_PATH}/jornada`);
								}}
							>
								Ver todos os passos
							</Button>
							<Button type="primary" onClick={fecharCelebracao}>
								{celebracao.jornadaConcluida
									? "Aproveitar"
									: "Vamos lá!"}
							</Button>
						</div>
					</div>
				)}
			</Modal>

			{mostrarIndicador && (
				<div
					style={{
						display: "flex",
						alignItems: "center",
						cursor: "pointer",
						marginLeft: isMobile ? 0 : "32px",
						marginRight: isMobile ? "auto" : 0,
						maxWidth: isMobile ? 180 : 640,
					}}
					onClick={() => setModalVisivel(true)}
				>
					<style>
						{`@keyframes jornadaFoguetePisca {
							0%, 100% { opacity: 1; }
							50% { opacity: 0.25; }
						}`}
					</style>
					<RocketOutlined
						style={{
							fontSize: isMobile ? "22px" : "18px",
							color: "#ff8034",
							marginRight: isMobile ? 0 : 8,
							animation: isMobile
								? "jornadaFoguetePisca 1.2s ease-in-out infinite"
								: undefined,
						}}
					/>
					{isMobile && (
						<Tag
							color="gold"
							style={{
								marginLeft: 8,
								display: "inline-flex",
								alignItems: "center",
								gap: 4,
							}}
						>
							<GiftOutlined />
							Ganhe R$40
						</Tag>
					)}
					{!isMobile && (
						<Tag color="orange" style={{ marginRight: 8 }}>
							Passo {Math.min(dados.concluidos + 1, dados.total)}/
							{dados.total}
						</Tag>
					)}
					{!isMobile && (
						<span
							style={{
								fontSize: "14px",
								whiteSpace: "nowrap",
								overflow: "hidden",
								textOverflow: "ellipsis",
							}}
						>
							{passo.titulo}
						</span>
					)}

					{!isMobile && (
						<InfoCircleOutlined
							style={{
								fontSize: 14,
								color: "#8c8c8c",
								marginLeft: 6,
							}}
						/>
					)}

					{!isMobile && (
						<Tag
							color="gold"
							style={{
								marginLeft: 12,
								display: "inline-flex",
								alignItems: "center",
								gap: 4,
							}}
						>
							<GiftOutlined />
							Conclua tudo e ganhe R$40
						</Tag>
					)}

					<Modal
						title={passo.titulo}
						open={modalVisivel}
						onCancel={(e) => {
							e && e.stopPropagation && e.stopPropagation();
							setModalVisivel(false);
						}}
						footer={[
							<Button
								key="todos"
								onClick={(e) => {
									e.stopPropagation();
									irParaJornada();
								}}
							>
								Ver todos os passos
							</Button>,
							<Button
								key="ok"
								type="primary"
								onClick={(e) => {
									e.stopPropagation();
									setModalVisivel(false);
								}}
							>
								Entendi
							</Button>,
						]}
						centered
						width={880}
						style={{ maxWidth: "92vw" }}
						destroyOnClose
					>
						{passo.descricao && (
							<div
								className="jornada-descricao-html"
								style={{ fontSize: 14 }}
								dangerouslySetInnerHTML={{
									__html: passo.descricao,
								}}
							/>
						)}
						<CarrosselImagens
							imagens={passo.imagens}
							style={{ marginTop: 16 }}
						/>
						{dados.total - dados.concluidos > 0 && (
							<div
								style={{
									marginTop: 16,
									padding: "10px 14px",
									background: "#fff7e6",
									border: "1px solid #ffd591",
									borderRadius: 8,
									color: "#ad6800",
									fontWeight: 600,
									fontSize: 14,
									display: "flex",
									alignItems: "center",
									gap: 8,
								}}
							>
								<GiftOutlined style={{ fontSize: 16 }} />
								{dados.total - dados.concluidos === 1
									? "Falta 1 passo para você ganhar R$40!"
									: `Faltam ${
											dados.total - dados.concluidos
									  } passos para você ganhar R$40!`}
							</div>
						)}
						{embedUrl && (
							<div style={{ marginTop: 12 }}>
								<iframe
									src={
										modalVisivel ? embedUrl : "about:blank"
									}
									title={passo.titulo}
									width="100%"
									height="380"
									frameBorder="0"
									allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
									allowFullScreen
								></iframe>
							</div>
						)}
					</Modal>
				</div>
			)}
		</>
	);
};

export default NavJornada;
